import { useState, useMemo, type ChangeEvent } from "react";
import { useLocalization } from "@fluent/react";
import { Button, Chip, Popover, TextInput } from "@narmi/design_system";
import styles from "./ColumnMappingScreen.module.scss";

type SavedMappingToolbarProps<
  M extends { id: string; name: string },
> = {
  savedMappings: M[];
  selectedMapping: M | null;
  hasUnsavedChanges: boolean;
  onSelectMapping: (mapping: M) => void;
  onSaveNew: () => void;
  onUpdateMapping: () => void;
  updateLoading: boolean;
  mappingsLoading: boolean;
};

const SavedMappingToolbar = <M extends { id: string; name: string }>({
  savedMappings,
  selectedMapping,
  hasUnsavedChanges,
  onSelectMapping,
  onSaveNew,
  onUpdateMapping,
  updateLoading,
  mappingsLoading,
}: SavedMappingToolbarProps<M>) => {
  const { l10n } = useLocalization();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredMappings = useMemo(() => {
    if (!searchTerm.trim()) return savedMappings;
    const query = searchTerm.trim().toLowerCase();
    return savedMappings.filter((mapping) =>
      mapping.name.toLowerCase().includes(query)
    );
  }, [savedMappings, searchTerm]);

  const handleOpen = () => {
    setSearchTerm("");
    setIsOpen(true);
  };

  const handleDismiss = () => {
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleSelect = (mapping: M) => {
    onSelectMapping(mapping);
    setIsOpen(false);
    setSearchTerm("");
  };

  const chipLabel = selectedMapping
    ? selectedMapping.name
    : l10n.getString("column-mapping-select-saved-mapping");

  const hasNoMappings = !mappingsLoading && savedMappings.length === 0;
  const hasNoSearchResults =
    !mappingsLoading && savedMappings.length > 0 && filteredMappings.length === 0;

  const popoverContent = (
    <div className={styles.savedMappingsDropdown}>
      <div className={styles.savedMappingsDropdownSearch}>
        <TextInput
          label={l10n.getString("column-mapping-search-mappings")}
          value={searchTerm}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setSearchTerm(e.target.value)
          }
        />
      </div>
      <div className={styles.savedMappingsDropdownDivider} />
      <div className={styles.savedMappingsDropdownList}>
        {mappingsLoading && (
          <div className={styles.savedMappingsDropdownLoading}>
            {l10n.getString("loading")}
          </div>
        )}
        {hasNoMappings && (
          <div className={styles.savedMappingsDropdownEmpty}>
            {l10n.getString("column-mapping-no-mappings-found")}
          </div>
        )}
        {hasNoSearchResults && (
          <div className={styles.savedMappingsDropdownEmpty}>
            {l10n.getString("column-mapping-no-mappings-found")}
          </div>
        )}
        {!mappingsLoading &&
          filteredMappings.map((mapping) => (
            <button
              key={mapping.id}
              type="button"
              className={`${styles.savedMappingsDropdownItem} ${
                selectedMapping?.id === mapping.id
                  ? styles.savedMappingsDropdownItemSelected
                  : ""
              }`}
              onClick={() => handleSelect(mapping)}
            >
              <span className={styles.savedMappingsDropdownItemLabel}>
                {mapping.name}
              </span>
            </button>
          ))}
      </div>
    </div>
  );

  return (
    <div className={styles.savedMappingToolbar}>
      <Popover
        content={popoverContent}
        alignment="start"
        isOpen={isOpen}
        onUserDismiss={handleDismiss}
      >
        <Chip
          kind="secondary"
          label={chipLabel}
          startIcon="bookmark"
          onClick={handleOpen}
          endIcon={`chevron-${isOpen ? "up" : "down"}`}
        />
      </Popover>
      <div className={styles.savedMappingActions}>
        {selectedMapping && hasUnsavedChanges && (
          <Button
            kind="tonal"
            size="xs"
            label={l10n.getString("column-mapping-update-mapping")}
            onClick={onUpdateMapping}
            disabled={updateLoading}
          />
        )}
        {!selectedMapping && (
          <Button
            kind="tonal"
            size="xs"
            label={l10n.getString("column-mapping-save-new-mapping")}
            onClick={onSaveNew}
          />
        )}
      </div>
    </div>
  );
};

export default SavedMappingToolbar;
